// ── useFCM.js ─────────────────────────────────────────────────────────────────
// Hook مركزي لإدارة Firebase Cloud Messaging
// - يطلب إذن الإشعارات من المستخدم
// - يسجّل Service Worker أولاً قبل طلب الـ token
// - يحفظ FCM token في Firebase تحت /fcm_tokens/{uid} للأعضاء
// - يستمع للرسائل في المقدمة ويعرضها كإشعارات حقيقية في شريط الهاتف
// - يوفر queuePushNotification لإرسال إشعار لمستخدم آخر عبر RTDB
// - يشغّل صوتاً مميزاً خاصاً بالموقع عند كل إشعار
// - يدعم تفضيلات الإشعارات المخصصة لكل مستخدم/زائر
// - يُزامن التفضيلات مع الـ Service Worker عبر postMessage
// - يعمل للزوار (بدون تسجيل دخول) في الشات العام وردود الدعم
// - يستقبل PLAY_NOTIFICATION_SOUND من SW لتشغيل الصوت المميز في الـ client
// - يفحص الصوت المعلق (pending_sound) من IndexedDB عند فتح الصفحة
// - يُشغّل RTDB listener في الـ Service Worker للإشعارات الخلفية الحقيقية

import { useEffect, useCallback, useRef } from "react";
import { ref, set, push, onValue, update } from "firebase/database";
import { db, getFCMToken, onForegroundMessage, registerFCMServiceWorker } from "../firebase";

const FCM_TOKENS_REF = "fcm_tokens";

// ── أنواع الإشعارات المدعومة ───────────────────────────────────────────────────
export const NOTIF_TYPES = {
  GLOBAL_CHAT:          "global_chat",          // رسائل الشات العام
  PRIVATE_MSG:          "private_message",       // رسائل خاصة
  FRIEND_REQUEST:       "friend_request",        // طلبات صداقة
  FRIEND_ACCEPT:        "friend_accepted",       // قبول الصداقة
  SUPPORT_REPLY:        "support_reply",         // رد الدعم/الشكاوي
  SYSTEM:               "system",               // إشعارات النظام العامة
  ADMIN_SUPPORT:        "admin_support",         // دعم جديد (للأدمن)
  ADMIN_COMPLAINT:      "admin_complaint",       // شكوى جديدة (للأدمن)
  ADMIN_PAYMENT:        "admin_payment",         // إثبات دفع جديد (للأدمن)
  PAYMENT_APPROVED:     "payment_approved",      // قبول الدفع (للمستخدم)
  PAYMENT_REJECTED:     "payment_rejected",      // رفض الدفع (للمستخدم)
  BROKEN_PACKAGE:       "broken_package",        // حزمة تالفة اكتُشفت (للأدمن)
  BROKEN_PACKAGE_FIXED: "broken_package_fixed",  // تم إصلاح حزمة تالفة (للأدمن)
};

// ── أنواع الإشعارات المتاحة للزوار (بدون تسجيل دخول) ─────────────────────────
export const GUEST_AVAILABLE_TYPES = new Set([
  NOTIF_TYPES.GLOBAL_CHAT,
  NOTIF_TYPES.SUPPORT_REPLY,
  NOTIF_TYPES.SYSTEM,
]);

// ── قراءة لغة المستخدم من localStorage ────────────────────────────────────────
export const getUserLang = () => {
  try {
    const saved = localStorage.getItem("app_language");
    if (saved === "ar" || saved === "en") return saved;
  } catch {}
  return "ar";
};

// ── اختيار النص المناسب من payload الإشعار حسب لغة المستخدم ──────────────────
// يدعم الحقول الثنائية (titleAr/titleEn/bodyAr/bodyEn) مع fallback للحقل الواحد
export const resolveNotifText = (notif) => {
  if (!notif) return { title: "Leaks Mercy", body: "" };
  const lang  = getUserLang();
  const isAr  = lang === "ar";
  const title = (isAr
    ? (notif.titleAr || notif.title || "")
    : (notif.titleEn || notif.title || "")) || "Leaks Mercy";
  const body  = isAr
    ? (notif.bodyAr  || notif.body  || "")
    : (notif.bodyEn  || notif.body  || "");
  return { title, body };
};

// ── قراءة تفضيلات الإشعارات من localStorage ─────────────────────────────────
export const getNotifPrefs = () => {
  try {
    const stored = localStorage.getItem("notif_prefs");
    if (stored) return JSON.parse(stored);
  } catch {}
  // الإعدادات الافتراضية: كل شيء مفعّل
  return {
    [NOTIF_TYPES.GLOBAL_CHAT]:           true,
    [NOTIF_TYPES.PRIVATE_MSG]:           true,
    [NOTIF_TYPES.FRIEND_REQUEST]:        true,
    [NOTIF_TYPES.FRIEND_ACCEPT]:         true,
    [NOTIF_TYPES.SUPPORT_REPLY]:         true,
    [NOTIF_TYPES.SYSTEM]:                true,
    [NOTIF_TYPES.ADMIN_SUPPORT]:         true,
    [NOTIF_TYPES.ADMIN_COMPLAINT]:       true,
    [NOTIF_TYPES.ADMIN_PAYMENT]:         true,
    [NOTIF_TYPES.PAYMENT_APPROVED]:      true,
    [NOTIF_TYPES.PAYMENT_REJECTED]:      true,
    [NOTIF_TYPES.BROKEN_PACKAGE]:        true,
    [NOTIF_TYPES.BROKEN_PACKAGE_FIXED]:  true,
  };
};

// ── حفظ تفضيلات الإشعارات ────────────────────────────────────────────────────
export const saveNotifPrefs = (prefs) => {
  try {
    localStorage.setItem("notif_prefs", JSON.stringify(prefs));
    syncPrefsWithSW(prefs);
  } catch {}
};

// ── مزامنة التفضيلات مع الـ Service Worker ───────────────────────────────────
export const syncPrefsWithSW = (prefs) => {
  if (!("serviceWorker" in navigator)) return;
  try {
    navigator.serviceWorker.ready.then((reg) => {
      if (reg && reg.active) {
        // ✅ إرسال التفضيلات مع اللغة الحالية ليستخدمها SW في الإشعارات
        const prefsWithLang = {
          ...(prefs || getNotifPrefs()),
          __lang__: getUserLang(),
        };
        reg.active.postMessage({
          type:  "UPDATE_NOTIF_PREFS",
          prefs: prefsWithLang,
        });
        // مزامنة تفضيل الصوت أيضاً
        reg.active.postMessage({
          type:    "UPDATE_SOUND_PREF",
          enabled: isNotifSoundEnabled(),
        });
      }
    }).catch(() => {});
  } catch {}
};

// ── إرسال uid للـ SW لتفعيل RTDB listener الخلفي ────────────────────────────
export const subscribeUserNotifsInSW = (uid) => {
  if (!uid || !("serviceWorker" in navigator)) return;
  try {
    navigator.serviceWorker.ready.then((reg) => {
      if (reg && reg.active) {
        reg.active.postMessage({
          type: "SUBSCRIBE_USER_NOTIFS",
          uid,
        });
      }
    }).catch(() => {});
  } catch {}
};

// ── التحقق هل نوع الإشعار مفعّل ─────────────────────────────────────────────
export const isNotifTypeEnabled = (type) => {
  const prefs = getNotifPrefs();
  if (type === undefined || type === null) return true;
  return prefs[type] !== false;
};

// ── التحقق هل الصوت مفعّل ────────────────────────────────────────────────────
export const isNotifSoundEnabled = () => {
  try { return localStorage.getItem("notif_sound") !== "false"; } catch { return true; }
};

// ── توليد صوت إشعار مميز بـ Web Audio API ─────────────────────────────────────
export const playNotificationSound = () => {
  if (!isNotifSoundEnabled()) return;
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    const notes = [
      { freq: 880,  start: 0,    dur: 0.12, gain: 0.35 },
      { freq: 1100, start: 0.1,  dur: 0.12, gain: 0.3  },
      { freq: 1320, start: 0.2,  dur: 0.18, gain: 0.25 },
    ];

    notes.forEach(({ freq, start, dur, gain }) => {
      const osc      = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gainNode.gain.setValueAtTime(0, ctx.currentTime + start);
      gainNode.gain.linearRampToValueAtTime(gain, ctx.currentTime + start + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    });

    setTimeout(() => { try { ctx.close(); } catch {} }, 600);
  } catch {}
};

// للتوافق مع الاستخدامات القديمة
const playNotificationSoundInternal = playNotificationSound;

// ── فحص الصوت المعلق في IndexedDB (تشغيله عند فتح الصفحة) ───────────────────
const checkPendingSound = async () => {
  if (!isNotifSoundEnabled()) return;
  try {
    const DB_NAME    = "leaksmercy_sw";
    const STORE_NAME = "prefs";
    const request    = indexedDB.open(DB_NAME, 1);
    request.onsuccess = (e) => {
      const idb = e.target.result;
      try {
        const tx  = idb.transaction(STORE_NAME, "readwrite");
        const str = tx.objectStore(STORE_NAME);
        const get = str.get("pending_sound");
        get.onsuccess = (ev) => {
          if (ev.target.result) {
            str.delete("pending_sound");
            tx.oncomplete = () => {
              idb.close();
              setTimeout(playNotificationSoundInternal, 300);
            };
          } else {
            idb.close();
          }
        };
        get.onerror = () => { idb.close(); };
      } catch { idb.close(); }
    };
  } catch {}
};

// ── عرض إشعار حقيقي عبر Service Worker registration ──────────────────────────
// ✅ هذا يضمن ظهور الإشعار في شريط الهاتف سواء كان الموقع مفتوحاً أو لا
export const showLocalNotification = (title, body, options = {}) => {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    // ✅ ضمان عدم إرسال إشعار بعنوان فارغ أبداً
    if (!title || !title.trim()) title = "Leaks Mercy";

    // التحقق من تفضيلات المستخدم
    const notifType = options.notifType || options.data?.type;
    if (notifType && !isNotifTypeEnabled(notifType)) return;

    // تشغيل الصوت المميز
    playNotificationSoundInternal();

    // ✅ استخدام Service Worker registration.showNotification دائماً
    // هذا هو الطريق الوحيد لضمان ظهور الإشعار في شريط الهاتف
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(title, {
          body,
          icon:               "/android-chrome-192x192.png",
          badge:              "/notification-badge.png",
          vibrate:            [200, 100, 200],
          tag:                options.tag  || "leaksmercy",
          data:               options.data || { url: options.url || "/" },
          silent:             false,
          requireInteraction: false,
          renotify:           true,
        });
      }).catch(() => {
        _showFallbackNotification(title, body, options);
      });
    } else {
      _showFallbackNotification(title, body, options);
    }
  } catch {}
};

// ── Fallback: إشعار مباشر عبر Notification API ───────────────────────────────
const _showFallbackNotification = (title, body, options = {}) => {
  try {
    const n = new Notification(title, {
      body,
      icon:    "/android-chrome-192x192.png",
      badge:   "/notification-badge.png",
      vibrate: [200, 100, 200],
      tag:     options.tag  || "leaksmercy",
      data:    options.data || {},
      silent:  false,
    });
    if (options.url) {
      n.onclick = () => {
        window.focus();
        window.location.href = options.url;
        n.close();
      };
    }
  } catch {}
};

// ── دالة مساعدة: كتابة رسالة إشعار في RTDB لتُرسَل للمستخدم المستهدف ─────────
export const queuePushNotification = async (targetUid, payload) => {
  try {
    if (!targetUid) return;
    const notifRef = ref(db, `notifications/${targetUid}/push`);
    await push(notifRef, {
      ...payload,
      createdAt: Date.now(),
      read:      false,
    });
  } catch {}
};

// ── إرسال إشعار للأدمن (المالكين) ────────────────────────────────────────────
export const notifyAdmins = async (adminUids, payload) => {
  try {
    if (!adminUids || adminUids.length === 0) return;
    const promises = adminUids.map((uid) => queuePushNotification(uid, payload));
    await Promise.allSettled(promises);
  } catch {}
};

// ── RTDB Notification Listener (Foreground) ───────────────────────────────────
// يستمع لقناة RTDB الخاصة بالمستخدم في المقدمة ويعرض الإشعارات
let _rtdbUnsub = null;
let _rtdbListenedUid = null;

const startRTDBNotifListener = (uid) => {
  if (!uid || _rtdbListenedUid === uid) return;

  // إلغاء الاستماع السابق إذا وُجد
  if (_rtdbUnsub) {
    try { _rtdbUnsub(); } catch {}
    _rtdbUnsub = null;
  }
  _rtdbListenedUid = uid;

  const cutoff = Date.now();
  const notifRef = ref(db, `notifications/${uid}/push`);

  _rtdbUnsub = onValue(notifRef, (snapshot) => {
    const data = snapshot.val();
    if (!data) return;

    Object.entries(data).forEach(([key, notif]) => {
      if (!notif || notif.read) return;
      if (notif.createdAt && notif.createdAt < cutoff) return;

      const notifType = notif.type;
      if (notifType && !isNotifTypeEnabled(notifType)) return;

      // ── اختيار النص حسب لغة المستخدم ─────────────────────────────────────
      const { title: resolvedTitle, body: resolvedBody } = resolveNotifText(notif);

      // عرض الإشعار في شريط الهاتف
      showLocalNotification(
        resolvedTitle,
        resolvedBody,
        {
          tag:       notif.tag || `rtdb-${key}`,
          url:       notif.url || "/",
          notifType,
          data:      { url: notif.url || "/", type: notifType },
        }
      );

      // تحديد الإشعار كمقروء
      try {
        update(ref(db, `notifications/${uid}/push/${key}`), {
          read:    true,
          shownAt: Date.now(),
        });
      } catch {}
    });
  });
};

const stopRTDBNotifListener = () => {
  if (_rtdbUnsub) {
    try { _rtdbUnsub(); } catch {}
    _rtdbUnsub = null;
    _rtdbListenedUid = null;
  }
};

// ── الـ Hook الرئيسي ──────────────────────────────────────────────────────────
const useFCM = (currentUser) => {
  const unsubForegroundRef = useRef(null);
  const tokenSavedRef      = useRef(false);
  const swMsgListenerRef   = useRef(null);

  // ── حفظ الـ token في Firebase (للأعضاء فقط) ──────────────────────────────
  const saveToken = useCallback(async (uid) => {
    if (!uid || tokenSavedRef.current) return;
    try {
      if (!("Notification" in window)) return;
      if (Notification.permission !== "granted") return;

      await registerFCMServiceWorker();
      const token = await getFCMToken();
      if (!token) return;

      await set(ref(db, `${FCM_TOKENS_REF}/${uid}`), {
        token,
        updatedAt: Date.now(),
        userAgent: navigator.userAgent.slice(0, 120),
        platform:  navigator.platform || "web",
        lang:      getUserLang(),
      });
      tokenSavedRef.current = true;

      syncPrefsWithSW(getNotifPrefs());

      // ✅ تفعيل RTDB listener في الـ SW للإشعارات الخلفية
      subscribeUserNotifsInSW(uid);
    } catch {}
  }, []);

  // ── طلب الإذن وتسجيل الـ token ────────────────────────────────────────────
  const requestPermissionAndSave = useCallback(async (uid) => {
    try {
      if (!("Notification" in window)) return false;
      if (Notification.permission === "granted") {
        if (uid) await saveToken(uid);
        return true;
      }
      if (Notification.permission === "denied") return false;
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        if (uid) await saveToken(uid);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [saveToken]);

  // ── الاستماع للرسائل في المقدمة من FCM ────────────────────────────────────
  const setupForegroundListener = useCallback(async () => {
    if (unsubForegroundRef.current) return;
    const unsub = await onForegroundMessage((payload) => {
      const { notification, data } = payload;
      // ── اختيار النص حسب لغة المستخدم ─────────────────────────────────────
      const lang  = getUserLang();
      const isAr  = lang === "ar";
      const title = isAr
        ? (data?.titleAr || data?.title || notification?.title || "Leaks Mercy")
        : (data?.titleEn || data?.title || notification?.title || "Leaks Mercy");
      const body  = isAr
        ? (data?.bodyAr  || data?.body  || notification?.body  || "")
        : (data?.bodyEn  || data?.body  || notification?.body  || "");
      const notifType = data?.type;
      if (notifType && !isNotifTypeEnabled(notifType)) return;
      showLocalNotification(title, body, {
        tag:       data?.tag  || "leaksmercy",
        url:       data?.url  || "/",
        data:      data || {},
        notifType: notifType,
      });
    });
    unsubForegroundRef.current = unsub;
  }, []);

  // ── الاستماع لرسائل SW (PLAY_NOTIFICATION_SOUND) ───────────────────────────
  const setupSWMessageListener = useCallback(() => {
    if (!("serviceWorker" in navigator)) return;
    if (swMsgListenerRef.current) return;

    const handler = (event) => {
      if (!event.data) return;
      if (event.data.type === "PLAY_NOTIFICATION_SOUND") {
        playNotificationSoundInternal();
      }
      if (event.data.type === "SW_ACTIVATED") {
        syncPrefsWithSW(getNotifPrefs());
      }
    };

    navigator.serviceWorker.addEventListener("message", handler);
    swMsgListenerRef.current = handler;
  }, []);

  // ── تهيئة FCM عند تسجيل الدخول ──────────────────────────────────────────
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous) {
      // الزوار: فقط المستمعات الأساسية
      if (Notification.permission === "granted") {
        setupForegroundListener();
        syncPrefsWithSW(getNotifPrefs());
        setupSWMessageListener();
        checkPendingSound();
      }
      return;
    }

    const uid   = currentUser.uid;
    const timer = setTimeout(async () => {
      if (Notification.permission === "granted") {
        await saveToken(uid);
      }
      await setupForegroundListener();
      syncPrefsWithSW(getNotifPrefs());
      setupSWMessageListener();
      checkPendingSound();

      // ✅ تفعيل RTDB listener في المقدمة
      if (Notification.permission === "granted") {
        startRTDBNotifListener(uid);
        subscribeUserNotifsInSW(uid);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [currentUser, saveToken, setupForegroundListener, setupSWMessageListener]);

  // ── إلغاء الاشتراك عند تغيير المستخدم ─────────────────────────────────────
  useEffect(() => {
    return () => {
      if (unsubForegroundRef.current) {
        try { unsubForegroundRef.current(); } catch {}
        unsubForegroundRef.current = null;
      }
      if (swMsgListenerRef.current) {
        try {
          navigator.serviceWorker.removeEventListener("message", swMsgListenerRef.current);
        } catch {}
        swMsgListenerRef.current = null;
      }
      stopRTDBNotifListener();
      tokenSavedRef.current = false;
    };
  }, [currentUser]);

  return { requestPermissionAndSave, showLocalNotification, queuePushNotification };
};

export default useFCM;
